import { useState, useEffect } from 'react';
import Cookies from 'universal-cookie';
import useApi from './useApi';
import { interpretApexFeatureResponse } from '../utils/apexFeatureInterpretation';

const cookies = new Cookies();

// Re-export for callers that imported parse from this module
export { parseApexFeatureStatusPayload } from '../utils/apexFeatureInterpretation';

/**
 * Fetches LMS Apex feature (GET user/v1/featureStatus/{_Id}/Apex) when Manage Subscriptions loads.
 *
 * Semantics (`details.Apex`):
 * - `0` — legacy / not on Apex PAYG program
 * - `1` — LMS + Apex invite (`isApexEligible` true → Activate PAYG banner path)
 * - UTC epoch (ms typical) — MUB purchased on Apex PAYG (`isPaygMubFromApexTimestamp` true → post-Stripe manage layout)
 *
 * @param {boolean} [skip=false] - When true, do not call the API
 * @returns {{ isApexEligible: boolean, isPaygMubFromApexTimestamp: boolean, apexStatusLoading: boolean, apexStatusError: Error|null, rawApexPayload: * }}
 */
export default function useApexFeatureStatus(skip = false) {
  const [isApexEligible, setIsApexEligible] = useState(false);
  const [isPaygMubFromApexTimestamp, setIsPaygMubFromApexTimestamp] = useState(false);
  const [apexStatusLoading, setApexStatusLoading] = useState(!skip);
  const [apexStatusError, setApexStatusError] = useState(null);
  const [rawApexPayload, setRawApexPayload] = useState(null);

  useEffect(() => {
    if (skip) {
      setApexStatusLoading(false);
      return undefined;
    }

    let cancelled = false;

    const run = async () => {
      const _Id = cookies.get('_Id');
      const accountId = cookies.get('accountId');
      if (!_Id || !accountId) {
        if (!cancelled) {
          setIsApexEligible(false);
          setIsPaygMubFromApexTimestamp(false);
          setApexStatusLoading(false);
          setApexStatusError(null);
        }
        return;
      }

      try {
        if (!cancelled) {
          setApexStatusLoading(true);
          setApexStatusError(null);
        }

        const body = await useApi('GET_APEX_FEATURE_STATUS', {}, {});

        if (cancelled) return;

        setRawApexPayload(body);
        const interpretation = interpretApexFeatureResponse(body);
        setIsApexEligible(interpretation.isLmsApexInviteActive);
        setIsPaygMubFromApexTimestamp(interpretation.isPaygMubPurchased);

        if (
          body != null &&
          interpretation.rawApexValue !== undefined &&
          interpretation.rawApexValue !== null &&
          interpretation.rawApexValue !== '' &&
          !interpretation.isLmsApexInviteActive &&
          !interpretation.isLegacyApexOff &&
          !interpretation.isPaygMubPurchased
        ) {
          console.warn('[useApexFeatureStatus] Unrecognized Apex feature response shape', body);
        }
      } catch (e) {
        if (!cancelled) {
          setApexStatusError(e);
          setIsApexEligible(false);
          setIsPaygMubFromApexTimestamp(false);
          setRawApexPayload(null);
        }
      } finally {
        if (!cancelled) {
          setApexStatusLoading(false);
        }
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [skip]);

  return {
    isApexEligible,
    isPaygMubFromApexTimestamp,
    apexStatusLoading,
    apexStatusError,
    rawApexPayload
  };
}
